import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from './pagination'

type PaginationState = {
	total: number
	limit: number
	offset: number
}

type PaginationMeta = {
	currentPage: number
	totalPages: number
	startIndex: number
	endIndex: number
	previousEnabled: boolean
	nextEnabled: boolean
}

type PaginationController = {
	state: PaginationMeta
	onNextClick(): void
	onPrevClick(): void
	onPageClick(page: number): void
}
type PaginationControllerProps = {
	limit?: number
	offset?: number
	total?: number
	onChange(params: { offset?: number; limit?: number }): void
}

type PaginationProps = {
	className?: string
	pagination: PaginationController
}

function PaginationControlled({
	className,
	pagination,
	...props
}: PaginationProps) {
	const { t } = useTranslation()
	const itemList = getItemsList(pagination.state)

	return (
		<Pagination {...props} className={className}>
			<PaginationContent>
				{pagination.state.previousEnabled && (
					<PaginationItem className='flex items-center'>
						<PaginationPrevious
							size='xs'
							text={t('app.previous', {
								defaultValue: 'Previous',
							})}
							data-testid='pagination-prev'
							onClick={pagination.onPrevClick}
						/>
					</PaginationItem>
				)}
				{itemList.map((page) => {
					if (typeof page === 'string') {
						return (
							<PaginationItem key={page} className='flex items-center'>
								<PaginationEllipsis />
							</PaginationItem>
						)
					}

					return (
						<PaginationItem
							className='flex items-center'
							key={page}
							data-testid={`pagination-page-${page}`}
							onClick={() => pagination.onPageClick(page - 1)}
						>
							<PaginationLink
								size='xs'
								isActive={pagination.state.currentPage === page - 1}
							>
								{page}
							</PaginationLink>
						</PaginationItem>
					)
				})}
				{pagination.state.nextEnabled && (
					<PaginationItem className='flex items-center'>
						<PaginationNext
							size='xs'
							text={t('app.next', {
								defaultValue: 'Next',
							})}
							data-testid='pagination-next'
							onClick={pagination.onNextClick}
						/>
					</PaginationItem>
				)}
			</PaginationContent>
		</Pagination>
	)
}

const getPreviousEnabled = (currentPage: number): boolean => currentPage > 0

const getNextEnabled = (currentPage: number, totalPages: number): boolean =>
	currentPage + 1 < totalPages

const getTotalPages = (totalItems: number, pageSize: number): number =>
	Math.ceil(totalItems / pageSize)

const getStartIndex = (pageSize: number, currentPage: number): number =>
	pageSize * currentPage

const getEndIndex = (
	pageSize: number,
	currentPage: number,
	totalItems: number
): number => {
	const lastPageEndIndex = pageSize * (currentPage + 1)

	if (lastPageEndIndex > totalItems) {
		return totalItems
	}

	return lastPageEndIndex
}

const limitPageBounds =
	(totalItems: number, pageSize: number) =>
	(page: number): number =>
		Math.min(Math.max(page, 0), getTotalPages(totalItems, pageSize) - 1)

const getPaginationMeta = ({
	total,
	limit,
	offset,
}: PaginationState): PaginationMeta => {
	const totalPages = getTotalPages(total, limit)
	const currentPage = totalPages - Math.ceil((total - offset) / limit)

	return {
		totalPages,
		currentPage,
		startIndex: getStartIndex(limit, currentPage),
		endIndex: getEndIndex(limit, currentPage, total),
		previousEnabled: getPreviousEnabled(currentPage),
		nextEnabled: getNextEnabled(currentPage, totalPages),
	}
}

function usePagination({
	limit = 100,
	offset = 0,
	total: initTotal,
	onChange,
}: PaginationControllerProps): PaginationController {
	const [total, setTotal] = useState(0)
	useEffect(() => {
		if (initTotal !== undefined) {
			setTotal(initTotal)
		}
	}, [initTotal])

	const state = getPaginationMeta({
		total,
		limit,
		offset,
	})
	const applyMeta = (page: number) =>
		onChange({
			offset: getStartIndex(limit, page),
			limit,
		})

	return {
		state,
		onNextClick: () =>
			applyMeta(limitPageBounds(total, limit)(state.currentPage + 1)),
		onPrevClick: () =>
			applyMeta(limitPageBounds(total, limit)(state.currentPage - 1)),
		onPageClick: (page) => applyMeta(limitPageBounds(total, limit)(page)),
	}
}

const range = (start: number, end: number) => {
	const length = end - start + 1

	return Array.from({ length }, (_, i) => start + i)
}
/**
 * Basic list of items to render
 * e.g. itemList = ['first', 'previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next', 'last']
 */
function getItemsList({
	totalPages,
	currentPage,
}: {
	totalPages: number
	currentPage: number
}) {
	const boundaryCount = 1
	const siblingCount = 1
	const page = currentPage + 1
	const startPages = range(1, Math.min(boundaryCount, totalPages))
	const endPages = range(
		Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
		totalPages
	)

	const siblingsStart = Math.max(
		Math.min(
			// Natural start
			page - siblingCount,
			// Lower boundary when page is high
			totalPages - boundaryCount - siblingCount * 2 - 1
		),
		// Greater than startPages
		boundaryCount + 2
	)

	const siblingsEnd = Math.min(
		Math.max(
			// Natural end
			page + siblingCount,
			// Upper boundary when page is low
			boundaryCount + siblingCount * 2 + 2
		),
		// Less than endPages
		endPages.length > 0 ? endPages[0] - 2 : totalPages - 1
	)

	return [
		...startPages,

		// Start ellipsis

		...(siblingsStart > boundaryCount + 2
			? ['start-ellipsis']
			: boundaryCount + 1 < totalPages - boundaryCount
				? [boundaryCount + 1]
				: []),

		// Sibling pages
		...range(siblingsStart, siblingsEnd),

		// End ellipsis

		...(siblingsEnd < totalPages - boundaryCount - 1
			? ['end-ellipsis']
			: totalPages - boundaryCount > boundaryCount
				? [totalPages - boundaryCount]
				: []),

		...endPages,
	]
}

export {
	PaginationControlled,
	type PaginationController,
	type PaginationControllerProps,
	type PaginationMeta,
	type PaginationProps,
	type PaginationState,
	usePagination,
}
